export const SITE_URL = 'https://www.nicedayfor.it';
export const DEFAULT_OG_IMAGE = '/og-image.jpg';
export const HOMEPAGE_TITLE = "Day For It? Australia's Park or Pub Weather Checker";
export const HOMEPAGE_DESCRIPTION =
  "Get a live yes-or-no call on whether it's a day for park beers or pub beers, plus nearby parks and pubs based on your location.";

export const HOMEPAGE_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Day For It',
    url: SITE_URL,
    inLanguage: 'en-AU',
    description: HOMEPAGE_DESCRIPTION,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Day For It',
    url: SITE_URL,
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web Browser',
    inLanguage: 'en-AU',
    description: HOMEPAGE_DESCRIPTION,
    author: {
      '@type': 'Person',
      name: 'James Maine',
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'AUD',
    },
  },
];

export function buildCitySchema(city) {
  const path = `/city/${city.slug}`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: city.title,
      description: city.description,
      url: `${SITE_URL}${path}`,
      inLanguage: 'en-AU',
      about: {
        '@type': 'Place',
        name: city.name,
        address: {
          '@type': 'PostalAddress',
          addressLocality: city.name,
          addressCountry: 'AU',
        },
      },
      isPartOf: {
        '@type': 'WebSite',
        name: 'Day For It',
        url: SITE_URL,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: city.name,
          item: `${SITE_URL}${path}`,
        },
      ],
    },
  ];
}

export function buildInfoPageSchema({ title, description, path }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: `${SITE_URL}${path}`,
    inLanguage: 'en-AU',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Day For It',
      url: SITE_URL,
    },
  };
}

/**
 * Full <head> metadata for a page, as React Router `meta()` descriptors.
 * Route `meta` replaces (doesn't merge with) parent meta, so every page returns the full set.
 * URLs are always absolute on the production domain, so previews never claim to be canonical.
 */
export function buildPageMeta({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  ogType = 'website',
  schema = null,
}) {
  const url = `${SITE_URL}${path}`;
  const imageUrl = `${SITE_URL}${image}`;
  const imageAlt = 'Day For It social card with park-or-pub weather verdict branding';

  return [
    { title },
    { name: 'title', content: title },
    { name: 'description', content: description },
    { name: 'author', content: 'James Maine' },
    { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' },
    { tagName: 'link', rel: 'canonical', href: url },

    { property: 'og:type', content: ogType },
    { property: 'og:url', content: url },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: imageUrl },
    { property: 'og:image:secure_url', content: imageUrl },
    { property: 'og:image:type', content: 'image/jpeg' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:alt', content: imageAlt },
    { property: 'og:site_name', content: 'Day For It' },
    { property: 'og:locale', content: 'en_AU' },

    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:url', content: url },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:image:alt', content: imageAlt },
    { name: 'twitter:site', content: '@utropics' },
    { name: 'twitter:creator', content: '@utropics' },

    ...(schema ? [{ 'script:ld+json': schema }] : []),
  ];
}

export const HOMEPAGE_META = buildPageMeta({
  title: HOMEPAGE_TITLE,
  description: HOMEPAGE_DESCRIPTION,
  path: '/',
  schema: HOMEPAGE_SCHEMA,
});

export const NOT_FOUND_META = buildPageMeta({
  title: 'Page Not Found',
  description: 'The page you were looking for does not exist.',
  path: '/not-found',
  noindex: true,
});
